import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import * as I from '../../../../../interfaces/merchants/products/purchases';
import * as Actions from '../../../../../redux/actions/merchants/products/purchases';
import PurchaseStep, { StepItems } from '../common/PurchaseStep';

import Auth from '../auth';
import Confirmation from '../confirmation';
import Customer from '../customer';
import CompletionFailed from '../failed/CompletionFailed';
import Payment from '../payment';
import Subscribed from '../subscribed';
import Completion from './completion';

interface StateProps {
  merchantProduct: I.MerchantProduct;
  publicId: string;
  step: number;
  postable: boolean;
  settings: I.SettingsSubscription;
}

interface DispatchProps {
  actions: {
    goTo: (step: number) => () => void;
    onSubmit: (step: number) => () => void;
    onFinalSubmit: (step: number) => () => void;
  };
}

type Props = StateProps & DispatchProps;

const FULL_WORKFLOW_STEPS = [
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_CUSTOMER_WORKFLOW_STEPS = [
  StepItems.Payment,
  StepItems.Confirmation,
];

const RETURN_TARGET_STEP = 97;
const RESTART_STEP = 98;
const SUBSCRIBED_STEP = 99;

class SchemeTypeSubscription extends React.Component<Props> {
  private renderAuthAt(step: number) {
    return (
      <Auth
        schemeType={this.props.settings.schemeType}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onSubmitSubscribed={this.props.actions.goTo(SUBSCRIBED_STEP)}
      />
    );
  }

  private renderCustomerAt(step: number) {
    return (
      <Customer
        schemeType={this.props.settings.schemeType}
        settings={this.props.settings.customer}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
      />
    );
  }

  private renderPaymentAt(step: number) {
    return (
      <Payment
        schemeType={this.props.settings.schemeType}
        settings={this.props.settings.payment}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
      />
    );
  }

  private renderConfirmationAt(step: number) {
    return (
      <Confirmation
        schemeType={this.props.settings.schemeType}
        product={this.props.merchantProduct}
        settings={this.props.settings.confirmation}
        postable={this.props.postable}
        onSubmit={this.props.actions.onFinalSubmit(step)}
        onRestart={this.props.actions.goTo(RESTART_STEP)}
        onReturnTargetStep={this.props.actions.goTo(RETURN_TARGET_STEP)}
        publicId={this.props.publicId}
      />
    );
  }

  private renderCompletion() {
    return (
      <Completion
        schemeType={this.props.settings.schemeType}
        settings={this.props.settings.completion}
        product={this.props.merchantProduct}
        publicId={this.props.publicId}
      />
    );
  }

  private renderSubscribed() {
    return (
      <Subscribed
        publicId={this.props.publicId}
        settings={this.props.settings.completion}
      />
    );
  }

  private renderFailedRestart() {
    return (
      <CompletionFailed
        schemeType={this.props.settings.schemeType}
        settings={this.props.settings.completion!}
      />
    );
  }

  private renderFailedReturnTargetStep(modifierStep: number) {
    const backTo = {
      label: 'purchases.schemeTypeSubscription.returnTargetStep',
      modifyPage: this.props.actions.goTo(modifierStep),
    };
    return (
      <CompletionFailed
        schemeType={this.props.settings.schemeType}
        settings={this.props.settings.completion!}
        backTo={backTo}
      />
    );
  }

  private fullWorkFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderCustomerAt(1)        },
      2: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => this.renderPaymentAt(2)         },
      3: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderConfirmationAt(3)    },
      4: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderFailedRestart()           },
      99: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderSubscribed()              },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }

  private skipCustomerFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => this.renderPaymentAt(1)         },
      2: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderConfirmationAt(2)    },
      3: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderFailedRestart()           },
      99: { displayProgress: false, currentProgress: null,                  renderFn: () => this.renderSubscribed()              },
    }[step];

    return [SKIP_CUSTOMER_WORKFLOW_STEPS, setting];
  }

  public render() {
    const [allSteps, setting] =
      this.props.settings.customer.customerFields!.length !== 0
        ? this.fullWorkFlowSetting()
        : this.skipCustomerFlowSetting();

    return (
      <>
        {setting.displayProgress && (
          <PurchaseStep
            allSteps={allSteps}
            current={allSteps.indexOf(setting.currentProgress) + 1}
          />
        )}
        {setting.renderFn()}
      </>
    );
  }
}

const mapStateProps = ({
  merchantsProductsPurchases,
}: {
  merchantsProductsPurchases: I.State;
}): StateProps => ({
  merchantProduct: merchantsProductsPurchases.merchantProduct!,
  settings: merchantsProductsPurchases.settings as I.SettingsSubscription,
  step: merchantsProductsPurchases.step!,
  publicId: merchantsProductsPurchases.publicId!,
  postable: merchantsProductsPurchases.postable!,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    goTo: (step: number) => () => Actions.goTo(step)(dispatch),
    onSubmit: (step: number) => () => Actions.nextStep(step)(dispatch),
    onFinalSubmit: (step: number) => () => Actions.finalStep(step)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(SchemeTypeSubscription);
